import { useEffect, useState } from 'react';
import { useCentralCalls } from '@src/rest/CentralCalls';

export interface LogHoursConsultantOption {
    id: string;
    name: string;
}

// Lets any mounted Hours panel refresh after hours are logged from elsewhere (e.g. the header clock).
export const OFFERING_HOURS_LOGGED_EVENT = 'corval-offering-hours-logged';

export interface OfferingHoursLoggedDetail {
    clientOfferingId: string;
}

export const notifyOfferingHoursLogged = ( clientOfferingId: string ) => {
    window.dispatchEvent( new CustomEvent<OfferingHoursLoggedDetail>( OFFERING_HOURS_LOGGED_EVENT, {
        detail: { clientOfferingId },
    } ) );
};

const getCaseInsensitiveValue = <T = unknown>( obj: unknown, key: string ): T | undefined => {
    if( !obj || typeof obj !== 'object' ) return undefined;
    const record = obj as Record<string, unknown>;
    const foundKey = Object.keys( record ).find( k => k.toLowerCase() === key.toLowerCase() );
    return foundKey ? ( record[ foundKey ] as T ) : undefined;
};

export const pickString = ( row: unknown, keys: string[] ): string => {
    for( const key of keys ){
        const value = getCaseInsensitiveValue( row, key );
        const normalized = `${ value ?? '' }`.trim();
        if( normalized ) return normalized;
    }
    return '';
};

export const pickNumber = ( row: unknown, keys: string[] ): number => {
    for( const key of keys ){
        const value = getCaseInsensitiveValue( row, key );
        if( value == null || value === '' ) continue;
        const parsed = typeof value === 'number' ? value : Number( value );
        if( !Number.isNaN( parsed ) ) return parsed;
    }
    return 0;
};

export const parseArrayPayload = ( response: any ): any[] => {
    if( Array.isArray( response ) ) return response;

    const candidates = [ response?.result, response?.Result, response?.data, response?.Data ];
    for( const candidate of candidates ){
        if( Array.isArray( candidate ) ) return candidate;
        if( typeof candidate === 'string' ){
            try {
                const parsed = JSON.parse( candidate );
                if( Array.isArray( parsed ) ) return parsed;
            } catch {
                // ignore malformed JSON wrappers
            }
        }
    }

    if( typeof response === 'string' ){
        try {
            const parsed = JSON.parse( response );
            if( Array.isArray( parsed ) ) return parsed;
        } catch {
            // ignore malformed JSON payload
        }
    }

    return [];
};

export const normalizeConsultants = ( consultantRows: any[], accountRows: any[] ): LogHoursConsultantOption[] => {
    const accountsById = new Map<string, any>();
    accountRows.forEach( row => {
        const id = pickString( row, [ 'Id' ] ).toLowerCase();
        if( id ) accountsById.set( id, row );
    } );

    return consultantRows
        .map( row => {
            const id = pickString( row, [ 'Id' ] );
            if( !id ) return null;

            const accountId = pickString( row, [ 'AccountId' ] ).toLowerCase();
            const account = accountId ? accountsById.get( accountId ) : undefined;
            const firstName = pickString( account, [ 'FirstName' ] );
            const lastName = pickString( account, [ 'LastName' ] );
            const email = pickString( account, [ 'EmailAddress', 'Email' ] );
            const name = `${ firstName } ${ lastName }`.trim() || email || 'Consultant';

            return { id, name };
        } )
        .filter( ( row ): row is LogHoursConsultantOption => row != null )
        .sort( ( a, b ) => a.name.localeCompare( b.name ) );
};

// Loads consultant options only when `enabled`, so callers that already have the list skip the fetch.
export const useConsultantOptions = ( enabled: boolean ): { consultants: LogHoursConsultantOption[]; isLoading: boolean } => {
    const { TableSelect } = useCentralCalls();

    const [ consultants, setConsultants ] = useState<LogHoursConsultantOption[]>( [] ),
          [ isLoading, setIsLoading ]     = useState( enabled );

    useEffect( () => {
        if( !enabled ) return;

        let cancelled = false;

        const load = async () => {
            setIsLoading( true );
            try {
                const [ consultantRows, accountRows ] = await Promise.all( [
                    TableSelect( 'Consultants', {} ),
                    TableSelect( 'Accounts', {} ),
                ] );
                if( !cancelled ){
                    setConsultants( normalizeConsultants( parseArrayPayload( consultantRows ), parseArrayPayload( accountRows ) ) );
                }
            } catch( err ){
                console.error( '[useConsultantOptions] failed to load consultants:', err );
            } finally {
                if( !cancelled ) setIsLoading( false );
            }
        };

        load();

        return () => {
            cancelled = true;
        };
        // NOTE: hook function identities from useCentralCalls are unstable across renders.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ enabled ] );

    return { consultants, isLoading };
};
